import {
  parsePageResult,
  type BaseControllerPageResult,
} from '@zoryn/components/grid'
import type { HttpRequest } from '@zoryn/network'
import { DEFAULT_QUERY_URL, buildDataSourcePath } from './constants'
import type {
  DataSourceValidateStatus,
  ParsedSqlField,
  SqlDataSource,
  SqlDataSourceField,
  ValidateDataSourceResult,
} from './types'

const TABLE_CONFIG_MODULE = '/system/table-config'
const FIELD_CONFIG_MODULE = '/system/field-config'

export interface BackendTableConfig {
  id?: number
  systemFlag?: string
  dataSourceFlag?: string
  dataSourceName?: string
  queryUrl?: string
  dataSourceType?: string
  validateTime?: string
  customSql?: string
  execSql?: string
  remark?: string
}

export interface BackendFieldConfig {
  id?: number
  tableConfigId?: number
  tableField?: string
  tableName?: string
  enable?: boolean
  columnType?: string
  sqlExpression?: string
}

export interface BackendParseField {
  field: string
  rawExpr: string
  columnType: string
}

export interface BackendValidateResult {
  id?: number
  name?: string
  ok: boolean
  message: string
  fields?: BackendParseField[]
}

export interface BackendTableConfigSql {
  path: string
  systemFlag: string
  dataSourceFlag: string
  dataSourceName?: string
  customSql?: string
  execSql?: string
}

export interface BackendTableConfigDetail {
  tableConfig: BackendTableConfig
  fields: BackendFieldConfig[]
}

function assertSuccess<T>(
  res: BaseControllerPageResult<T>,
  fallbackMsg = '请求失败'
) {
  if (res.success === false) {
    throw new Error(res.msg ?? fallbackMsg)
  }
  return res
}

function toValidateStatus(validateTime?: string): DataSourceValidateStatus {
  return validateTime ? 'success' : 'idle'
}

export function mapFieldFromBackend(
  row: BackendFieldConfig
): SqlDataSourceField {
  return {
    field: row.tableField ?? '',
    label: row.tableName ?? row.tableField ?? '',
    rawExpr: row.sqlExpression,
    type: row.columnType ?? 'input',
    enabled: row.enable !== false,
  }
}

export function mapFieldToBackend(
  field: SqlDataSourceField,
  tableConfigId?: number
): BackendFieldConfig {
  return {
    tableConfigId,
    tableField: field.field,
    tableName: field.label,
    enable: field.enabled,
    columnType: field.type,
    sqlExpression: field.rawExpr,
  }
}

export function mapTableConfigFromBackend(
  row: BackendTableConfig,
  fields: SqlDataSourceField[] = []
): SqlDataSource {
  return {
    id: String(row.id ?? ''),
    system: row.systemFlag ?? '',
    dataSourceFlag: row.dataSourceFlag ?? '',
    name: row.dataSourceName ?? '',
    sql: row.customSql ?? '',
    description: row.remark,
    fields,
    validateStatus: toValidateStatus(row.validateTime),
    validatedAt: row.validateTime,
  }
}

export function mapTableConfigToBackend(
  source: SqlDataSource
): BackendTableConfig {
  const id =
    source.id && !source.id.startsWith('ds-') ? Number(source.id) : undefined
  return {
    id: Number.isFinite(id) ? id : undefined,
    systemFlag: source.system,
    dataSourceFlag: source.dataSourceFlag,
    dataSourceName: source.name,
    queryUrl: DEFAULT_QUERY_URL,
    customSql: source.sql,
    remark: source.description,
    dataSourceType: 'sql',
  }
}

function mapParseFields(rows: BackendParseField[]): ParsedSqlField[] {
  return rows.map((item) => ({
    field: item.field,
    rawExpr: item.rawExpr,
  }))
}

function mapParseFieldsToConfig(
  rows: BackendParseField[]
): SqlDataSourceField[] {
  return rows.map((item) => ({
    field: item.field,
    label: item.field,
    rawExpr: item.rawExpr,
    type: item.columnType ?? 'input',
    enabled: true,
  }))
}

function mapValidateResult(
  result: BackendValidateResult
): ValidateDataSourceResult {
  return {
    ok: result.ok,
    message: result.message,
    fields: result.fields ? mapParseFields(result.fields) : undefined,
  }
}

export async function fetchFieldConfigList(
  request: HttpRequest,
  tableConfigId: number
): Promise<SqlDataSourceField[]> {
  const res = await request.get<BaseControllerPageResult<BackendFieldConfig[]>>(
    {
      url: `${FIELD_CONFIG_MODULE}/listByTableConfigId`,
      params: { tableConfigId },
    }
  )
  assertSuccess(res)
  const rows = Array.isArray(res.data) ? res.data : []
  return rows.map(mapFieldFromBackend)
}

export async function fetchTableConfigDetail(
  request: HttpRequest,
  id: number
): Promise<SqlDataSource> {
  const res = await request.get<
    BaseControllerPageResult<BackendTableConfigDetail>
  >({
    url: `${TABLE_CONFIG_MODULE}/queryDetailById`,
    params: { id },
  })
  assertSuccess(res)
  const detail = res.data
  if (!detail?.tableConfig) {
    throw new Error('数据源不存在')
  }
  const fields = (detail.fields ?? []).map(mapFieldFromBackend)
  return mapTableConfigFromBackend(detail.tableConfig, fields)
}

export async function fetchTableConfigByPath(
  request: HttpRequest,
  path: string
): Promise<SqlDataSource> {
  const res = await request.get<
    BaseControllerPageResult<BackendTableConfigDetail>
  >({
    url: `${TABLE_CONFIG_MODULE}/queryDetailByPath`,
    params: { path },
  })
  assertSuccess(res)
  const detail = res.data
  if (!detail?.tableConfig) {
    throw new Error('数据源不存在')
  }
  const fields = (detail.fields ?? []).map(mapFieldFromBackend)
  return mapTableConfigFromBackend(detail.tableConfig, fields)
}

export async function parseSqlFields(
  request: HttpRequest,
  customSql: string
): Promise<SqlDataSourceField[]> {
  const res = await request.post<BaseControllerPageResult<BackendParseField[]>>(
    {
      url: `${TABLE_CONFIG_MODULE}/parseSqlFields`,
      params: { customSql },
    }
  )
  assertSuccess(res)
  return mapParseFieldsToConfig(Array.isArray(res.data) ? res.data : [])
}

export async function validateTableConfig(
  request: HttpRequest,
  options: {
    id?: number
    customSql?: string
    name?: string
  }
): Promise<ValidateDataSourceResult> {
  const res = await request.post<
    BaseControllerPageResult<BackendValidateResult>
  >({
    url: `${TABLE_CONFIG_MODULE}/validate`,
    params: {
      id: options.id,
      customSql: options.customSql,
    },
  })
  assertSuccess(res)
  return mapValidateResult(res.data)
}

export async function batchValidateTableConfigs(
  request: HttpRequest,
  ids: number[]
): Promise<
  Array<{ id: string; name: string; result: ValidateDataSourceResult }>
> {
  const res = await request.post<
    BaseControllerPageResult<BackendValidateResult[]>
  >({
    url: `${TABLE_CONFIG_MODULE}/batchValidate`,
    params: { ids },
  })
  assertSuccess(res)
  const rows = Array.isArray(res.data) ? res.data : []
  return rows.map((item) => ({
    id: String(item.id ?? ''),
    name: item.name ?? '',
    result: mapValidateResult(item),
  }))
}

export async function saveTableConfigWithFields(
  request: HttpRequest,
  source: SqlDataSource
): Promise<SqlDataSource> {
  const res = await request.post<BaseControllerPageResult<BackendTableConfig>>({
    url: `${TABLE_CONFIG_MODULE}/saveWithFields`,
    params: {
      tableConfig: mapTableConfigToBackend(source),
      fields: source.fields.map((field) => mapFieldToBackend(field)),
    },
  })
  assertSuccess(res)
  const saved = res.data
  if (!saved?.id) {
    throw new Error('保存失败：未返回 id')
  }
  return fetchTableConfigDetail(request, saved.id)
}

export async function fetchTableConfigList(
  request: HttpRequest
): Promise<SqlDataSource[]> {
  const res = await request.get<BaseControllerPageResult<BackendTableConfig[]>>(
    {
      url: `${TABLE_CONFIG_MODULE}/queryByExamplePage`,
      params: {
        currentPage: 1,
        pageSize: 9999,
        pageFlag: false,
      },
    }
  )
  assertSuccess(res)
  const { list } = parsePageResult(res)
  const sources = list.map((row) => mapTableConfigFromBackend(row, []))

  await Promise.all(
    sources.map(async (source) => {
      if (!source.id) return
      try {
        source.fields = await fetchFieldConfigList(request, Number(source.id))
      } catch {
        source.fields = []
      }
    })
  )

  return sources
}

export { buildDataSourcePath }
